/**
 * Modal Routes Configuration
 *
 * Declarative modal routing system similar to react-router.
 * Add new modal routes here without modifying other code.
 */

import React from "react";
import type { ModalRoute } from "@/types/modal";

// Lazy imports for modal components
const ProductModal = React.lazy(() =>
  import("@/components/organisms/ProductPage").then((module) => ({
    default: module.ProductPage,
  }))
);
const PreorderForm = React.lazy(() =>
  import("@/components/organisms/PreorderForm").then((module) => ({
    default: module.PreorderForm,
  }))
);
const SearchModal = React.lazy(() =>
  import("@/components/organisms/SearchModal").then((module) => ({
    default: module.SearchModal,
  }))
);
const FilterModal = React.lazy(() =>
  import("@/components/organisms/FilterModal").then((module) => ({
    default: module.FilterModal,
  }))
);

/**
 * Modal Routes Configuration
 *
 * Define modal routes declaratively:
 * - path: URL pattern with parameters
 * - component: React component to render
 * - modalOptions: ModalWindow configuration
 * - getProps: generate component props from URL params
 */
export const modalRoutes: ModalRoute[] = [
  {
    path: "/product/:id",
    component: ProductModal,
    modalOptions: {
      maxWidth: "x2l",
      closeOnOverlayClick: true,
      closeOnEscape: true,
      showCloseButton: true,
    },
    getProps: (params, context) => ({
      productId: params.id,
      onClose: context.onClose,
    }),
  },

  {
    path: "/product",
    component: ProductModal,
    modalOptions: {
      maxWidth: "x2l",
      closeOnOverlayClick: true,
      closeOnEscape: true,
      showCloseButton: true,
    },
    getProps: (_params, context) => ({
      productId: undefined,
      onClose: context.onClose,
    }),
  },

  {
    path: "/order",
    component: PreorderForm,
    modalOptions: {
      anchor: "right",
      maxWidth: "md",
      closeOnOverlayClick: true,
      closeOnEscape: true,
      showCloseButton: true,
    },
    getProps: (_params, context) => ({
      onEditItem: (itemId: string) => {
        console.log("Edit item:", itemId);
        context.navigateToModal("product", itemId);
      },
      onAddMore: () => {
        context.onClose();
      },
      onItemSelect: (itemId: string) => {
        context.navigateToModal("product", itemId);
      },
      onClose: context.onClose,
    }),
  },

  {
    path: "/search",
    component: SearchModal,
    modalOptions: {
      maxWidth: "lg",
      closeOnOverlayClick: true,
      closeOnEscape: true,
      showCloseButton: true,
    },
    getProps: (_params, context) => ({
      onClose: context.onClose,
    }),
  },

  {
    path: "/filter",
    component: FilterModal,
    modalOptions: {
      maxWidth: "md",
      closeOnOverlayClick: true,
      closeOnEscape: true,
      showCloseButton: true,
    },
    getProps: (_params, context) => ({
      onClose: context.onClose,
    }),
  },
];

/**
 * Get modal routes configuration
 */
export const getModalRoutes = (): ModalRoute[] => modalRoutes;

/**
 * Helper to add new modal route at runtime (for plugins, etc.)
 */
export const addModalRoute = (route: ModalRoute): void => {
  modalRoutes.push(route);
};

/**
 * Helper to remove modal route by path
 */
export const removeModalRoute = (path: string): boolean => {
  const index = modalRoutes.findIndex((route) => route.path === path);
  if (index !== -1) {
    modalRoutes.splice(index, 1);
    return true;
  }
  return false;
};
